import { useEffect, useState } from "react";
import { Archive, ArrowLeftRight, Check, Gift } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { api, ApiError } from "../lib/api";
import { rm, fmtDateTimeDMY, credits } from "../lib/format";
import { MOBILE_STICKY_BAR_PAD } from "../components/shell/MobileStickyBar";
import { EASE } from "../lib/motion";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { ProductImage } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { Badge, Button, EmptyState, PageLoader } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";
import { CardBack, Particles } from "../components/fx";
import { RevealStage, type PullOutcome } from "../components/packs";
import { useBodyLock } from "../hooks/useBodyLock";
import { VaultActionSheet } from "../components/vault/VaultActionSheet";
import { VaultItemModal, type VaultItemDetail } from "../components/vault/VaultItemModal";

interface VaultItemDto {
  id: string;
  productId: string;
  name: string;
  image: string | null;
  estValue: number;
  buybackCredits: number | null;
  buybackPercent: number | null;
  packId: string | null;
  packName: string | null;
  canBuyback: boolean;
  sourceType: string;
  status: string;
  shipmentOrderId: string | null;
  acquiredAt: string;
  flexxiPlan?: { id: string; status: string; paidTotal: number; totalPayable: number } | null;
}

interface PullDto {
  id: string;
  packName: string;
  tierName: string;
  tierColor: string;
  productName: string;
  productImage: string | null;
  categoryBackingImageUrl?: string | null;
  value: number;
  revealed: boolean;
  proof: { serverSeedHash: string; serverSeed: string; clientSeed: string; nonce: number };
  at: string;
}

interface TradeOfferDto {
  id: string;
  status: string;
  fromUserId: string;
  fromName: string;
  toUserId: string;
  toName: string;
  offerCredits: number;
  requestCredits: number;
  message: string | null;
  createdAt: string;
  expiresAt: string | null;
  recipientAcceptedAt: string | null;
  offerItems: { vaultItemId: string; name: string; image: string | null; value: number }[];
  requestItems: { vaultItemId: string; name: string; image: string | null; value: number }[];
}

type Tab = "flexxi" | "vault" | "pulls" | "trades";

export function Inventory() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, moduleEnabled, config } = useAuth();
  const vaultOn = moduleEnabled("vault");
  const packsOn = moduleEnabled("packs");
  const flexxiOn = Boolean(vaultOn && moduleEnabled("instalments") && config?.flexxiEnabled);
  const tradingOn = vaultOn && moduleEnabled("trading");
  const [tab, setTab] = useState<Tab>("vault");
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailItem, setDetailItem] = useState<VaultItemDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [revealFx, setRevealFx] = useState<PullDto | null>(null);

  const { data: vault, isLoading: vaultLoading } = useQuery({
    queryKey: ["vault"],
    queryFn: () => api<{ items: VaultItemDto[] }>("/vault"),
  });

  const { data: pulls, isLoading: pullsLoading } = useQuery({
    queryKey: ["my-pulls"],
    queryFn: () => api<{ items: PullDto[] }>("/packs/mine/pulls").catch(() => ({ items: [] as PullDto[] })),
    enabled: packsOn,
  });

  const { data: flexxiSlotsData } = useQuery({
    queryKey: ["flexxi-templates-slots"],
    queryFn: () => api<{ slots: { active: number; limit: number; reputationScore: number } }>("/flexxi/templates"),
    enabled: flexxiOn,
  });

  const { data: trades } = useQuery({
    queryKey: ["trades"],
    queryFn: () => api<{ items: TradeOfferDto[] }>("/trades").catch(() => ({ items: [] as TradeOfferDto[] })),
    enabled: tradingOn,
  });

  const unrevealed = pulls?.items.filter((p) => !p.revealed) ?? [];
  const incomingPending =
    trades?.items.filter((t) => t.status === "PENDING" && t.toUserId === user?.id) ?? [];
  const awaitingConfirm =
    trades?.items.filter((t) => t.status === "AWAITING_CONFIRM" && t.fromUserId === user?.id) ?? [];

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "trades" && tradingOn) setTab("trades");
    else if (tabParam === "pulls" && packsOn) setTab("pulls");
    else if (tabParam === "vault") setTab("vault");
    else if ((tabParam === "flexxi" || tabParam === "flexxivault") && flexxiOn) setTab("flexxi");
  }, [searchParams, tradingOn, packsOn, flexxiOn]);

  useEffect(() => {
    if (!multiSelectMode) setSelected(new Set());
  }, [multiSelectMode]);

  async function reveal(pull: PullDto) {
    try {
      await api(`/packs/pulls/${pull.id}/reveal`, { body: {} });
      setRevealFx(pull);
      await queryClient.invalidateQueries({ queryKey: ["my-pulls"] });
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't reveal");
    }
  }

  async function respondTrade(tradeId: string, action: "accept" | "decline" | "cancel" | "confirm") {
    setBusy(true);
    try {
      if (action === "cancel") {
        await api(`/trades/${tradeId}/cancel`, { body: {} });
      } else if (action === "confirm") {
        await api(`/trades/${tradeId}/confirm`, { body: {} });
      } else {
        await api(`/trades/${tradeId}/respond`, { body: { accept: action === "accept" } });
      }
      const messages = {
        accept: "Accepted — waiting for their final confirm",
        decline: "Trade declined",
        cancel: "Trade cancelled",
        confirm: "Trade completed!",
      } as const;
      toast("success", messages[action]);
      await queryClient.invalidateQueries({ queryKey: ["trades"] });
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Trade action failed");
    } finally {
      setBusy(false);
    }
  }

  function tradeStatusLabel(trade: TradeOfferDto, incoming: boolean): string {
    if (trade.status === "PENDING") return incoming ? "Awaiting your response" : "Pending";
    if (trade.status === "AWAITING_CONFIRM") return incoming ? "Awaiting their confirm" : "Confirm to complete";
    return trade.status.replace(/_/g, " ");
  }

  function tradeStatusColor(status: string): "amber" | "green" | "gray" | "pink" {
    if (status === "PENDING" || status === "AWAITING_CONFIRM") return "amber";
    if (status === "ACCEPTED") return "green";
    if (status === "EXPIRED") return "gray";
    return "gray";
  }

  function formatExpiry(expiresAt: string | null): string | null {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const hours = Math.floor(ms / 3_600_000);
    const mins = Math.floor((ms % 3_600_000) / 60_000);
    if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h left`;
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  }

  const flexxiHoldItems = vault?.items.filter((i) => i.status === "FLEXXI_HOLD") ?? [];
  const vaultCollectionItems = vault?.items.filter((i) => i.status !== "FLEXXI_HOLD") ?? [];
  const slotActive = flexxiSlotsData?.slots.active ?? flexxiHoldItems.length;
  const slotLimit = flexxiSlotsData?.slots.limit ?? 0;
  const emptyFlexxiSlots = Math.max(0, slotLimit - slotActive);
  const reservedFlexxiSlots = Math.max(0, slotActive - flexxiHoldItems.length);

  const tabs: { key: Tab; label: string; show: boolean; badge?: number }[] = [
    { key: "flexxi", label: "Flexxi vault", show: flexxiOn, badge: flexxiHoldItems.length || undefined },
    { key: "vault", label: vaultOn ? "Vault" : "Items", show: true },
    { key: "pulls", label: "Pack pulls", show: packsOn, badge: unrevealed.length || undefined },
    { key: "trades", label: "Trades", show: tradingOn, badge: (incomingPending.length + awaitingConfirm.length) || undefined },
  ];
  const visibleTabs = tabs.filter((t) => t.show);
  const activeTab = visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0]?.key ?? "vault";

  if (vaultLoading || (packsOn && pullsLoading)) return <PageLoader />;

  return (
    <div className={`space-y-4 px-4 pt-4 lg:px-6 ${MOBILE_STICKY_BAR_PAD}`}>
      <PullRevealOverlay pull={revealFx} onClose={() => setRevealFx(null)} />
      <VaultItemModal
        item={detailItem}
        vaultOn={vaultOn}
        tradingOn={tradingOn}
        creditsEnabled={Boolean(config?.creditsEnabled)}
        onClose={() => setDetailItem(null)}
        onTradeCreated={() => setTab("trades")}
      />

      <PageHeader
        title="Collection"
        description={
          flexxiOn
            ? "Flexxi vault, shipped cards, pack pulls, and trades — manage everything in one place."
            : "Your vault, pack pulls, and trades — ship items or flip unrevealed cards anytime."
        }
      />

      {/* Tabs */}
      <div className="flex gap-1.5 rounded-full bg-surface p-1">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`relative flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
              activeTab === t.key ? "bg-gradient-brand text-white" : "text-white/50"
            }`}
          >
            {t.label}
            {t.badge && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-pink px-1 text-[9px] font-bold">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Flexxi vault — instalment slots + cards on hold */}
      {activeTab === "flexxi" && flexxiOn && (
        <>
          <div className="rounded-2xl border border-white/5 bg-surface/70 px-3.5 py-3">
            <p className="text-xs font-medium text-white/85">
              {slotActive} of {slotLimit} FlexxiPlan slot{slotLimit === 1 ? "" : "s"} in use
            </p>
            <p className="mt-0.5 text-[10px] text-white/45">
              {emptyFlexxiSlots > 0
                ? `${emptyFlexxiSlots} empty slot${emptyFlexxiSlots === 1 ? "" : "s"} available for a new plan.`
                : "All slots are in use — complete a plan to free a slot."}
              {flexxiSlotsData?.slots.reputationScore != null
                ? ` · Reputation score ${flexxiSlotsData.slots.reputationScore}`
                : ""}
            </p>
            <Link to="/account/flexxi" className="mt-1.5 inline-block text-[11px] font-semibold text-brand-pink underline">
              View payments &amp; receipts
            </Link>
          </div>
          {flexxiHoldItems.length === 0 && emptyFlexxiSlots === 0 && reservedFlexxiSlots === 0 ? (
            <EmptyState
              icon={Archive}
              title="No FlexxiPlan items"
              hint="Start a FlexxiPlan from the shop — cards under instalment appear here."
            />
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {flexxiHoldItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() =>
                    setDetailItem({
                      id: item.id,
                      name: item.name,
                      image: item.image,
                      estValue: item.estValue,
                      buybackCredits: item.buybackCredits,
                      buybackPercent: item.buybackPercent,
                      packName: item.packName,
                      canBuyback: item.canBuyback,
                      status: item.status,
                      flexxiPlan: item.flexxiPlan ?? null,
                    })
                  }
                  className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-surface text-left transition-all lg:hover:-translate-y-1 lg:hover:border-amber-400/40"
                >
                  <ProductImage src={item.image} alt="" className="aspect-square w-full" />
                  <div className="p-2.5">
                    <ListingTitle className="text-[11px] leading-4">{item.name}</ListingTitle>
                    <div className="mt-1 flex flex-col gap-0.5">
                      <span className="text-[10px] text-white/45">{rm(item.estValue)}</span>
                      {item.flexxiPlan ? (
                        <span className="text-[10px] font-medium text-amber-200/90">
                          {rm(item.flexxiPlan.paidTotal)} / {rm(item.flexxiPlan.totalPayable)} paid · tap to pay
                        </span>
                      ) : null}
                      <Badge color="amber">Flexxi instalments</Badge>
                    </div>
                  </div>
                </button>
              ))}
              {Array.from({ length: reservedFlexxiSlots }).map((_, i) => (
                <FlexxiSlotPlaceholder key={`reserved-${i}`} title="Reserved slot" hint="Complete your first payment to unlock this card." />
              ))}
              {Array.from({ length: emptyFlexxiSlots }).map((_, i) => (
                <FlexxiSlotPlaceholder key={`empty-${i}`} title="Empty slot" hint="Available for a new FlexxiPlan." />
              ))}
            </div>
          )}
        </>
      )}

      {/* Vault / items */}
      {activeTab === "vault" && (
        <>
          {!vaultCollectionItems.length ? (
            <EmptyState
              icon={Archive}
              title={vaultOn ? "Vault is empty" : "No items yet"}
              hint={
                vaultOn
                  ? packsOn
                    ? "Pack pulls land here — ship them whenever you like."
                    : "Ship items from your collection whenever you like."
                  : "Items are shipped to you automatically."
              }
            />
          ) : (
            <>
              {vaultOn && (
                <div className="flex items-center justify-between rounded-2xl border border-white/5 bg-surface/70 px-3.5 py-2.5">
                  <div>
                    <p className="text-xs font-medium text-white/80">Select multiple</p>
                    <p className="text-[10px] text-white/40">
                      {multiSelectMode ? "Tap cards to bulk ship, trade, or buyback" : "Tap a card to view details"}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={multiSelectMode}
                    onClick={() => setMultiSelectMode((v) => !v)}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                      multiSelectMode ? "bg-brand-pink" : "bg-white/10"
                    }`}
                  >
                    <span
                      className={`pointer-events-none h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                        multiSelectMode ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              )}
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {vaultCollectionItems.map((item) => {
                const inVault = item.status === "IN_VAULT";
                const inTrade = item.status === "IN_TRADE";
                const selectable = inVault && vaultOn && multiSelectMode;
                const openDetail = inVault || inTrade || !vaultOn;
                const checked = selected.has(item.id);
                return (
                  <button
                    key={item.id}
                    disabled={!openDetail && !selectable}
                    onClick={() => {
                      if (multiSelectMode && selectable) {
                        const next = new Set(selected);
                        if (checked) next.delete(item.id);
                        else next.add(item.id);
                        setSelected(next);
                        return;
                      }
                      setDetailItem({
                        id: item.id,
                        name: item.name,
                        image: item.image,
                        estValue: item.estValue,
                        buybackCredits: item.buybackCredits,
                        buybackPercent: item.buybackPercent,
                        packName: item.packName,
                        canBuyback: item.canBuyback,
                        status: item.status,
                        flexxiPlan: item.flexxiPlan ?? null,
                      });
                    }}
                    className={`relative overflow-hidden rounded-2xl border bg-surface text-left transition-all lg:hover:-translate-y-1 ${
                      checked ? "border-brand-pink glow-pink" : "border-white/5 lg:hover:border-brand-pink/30"
                    } ${!openDetail && !selectable ? "opacity-60" : ""}`}
                  >
                    {multiSelectMode && checked && (
                      <span className="absolute right-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-brand-pink text-white">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                    <ProductImage src={item.image} alt="" className="aspect-square w-full" />
                    <div className="p-2.5">
                      <ListingTitle className="text-[11px] leading-4">{item.name}</ListingTitle>
                      <div className="mt-1 flex flex-col gap-0.5">
                        <span className="text-[10px] text-white/45">{rm(item.estValue)}</span>
                        {item.canBuyback && item.buybackCredits ? (
                          <span className="text-[10px] font-medium text-brand-pink">Buyback {credits(item.buybackCredits)}</span>
                        ) : null}
                        <Badge
                          color={
                            inVault
                              ? "green"
                              : inTrade
                                ? "pink"
                                : item.status === "SHIPMENT_REQUESTED"
                                  ? "amber"
                                  : "gray"
                          }
                        >
                          {inTrade ? "In trade" : item.status.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            </>
          )}
          {vaultOn && multiSelectMode && selected.size > 0 && vault && (
            <VaultActionSheet
              selectedItems={vaultCollectionItems
                .filter((item) => selected.has(item.id))
                .map((item) => ({
                  id: item.id,
                  name: item.name,
                  estValue: item.estValue,
                  buybackCredits: item.buybackCredits,
                  canBuyback: item.canBuyback,
                }))}
              tradingOn={tradingOn}
              creditsEnabled={Boolean(config?.creditsEnabled)}
              onClear={() => setSelected(new Set())}
              onTradeCreated={() => setTab("trades")}
            />
          )}
        </>
      )}

      {/* Pulls */}
      {activeTab === "pulls" && (
        <>
          {!pulls?.items.length ? (
            <EmptyState icon={Gift} title="No pack pulls yet" hint="Open a mystery pack to start your collection." />
          ) : (
            <div className="space-y-5">
              {/* Sealed pulls stay face-down until you turn them over. */}
              {unrevealed.length > 0 && (
                <div>
                  <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-brand-pink">
                    {unrevealed.length} card{unrevealed.length > 1 ? "s" : ""} waiting to be flipped
                  </p>
                  <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-6">
                    {unrevealed.map((pull, i) => (
                      <motion.button
                        key={pull.id}
                        onClick={() => reveal(pull)}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.4, ease: EASE.outExpo }}
                        whileHover={{ y: -8, scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="group relative"
                        aria-label={`Reveal your pull from ${pull.packName}`}
                      >
                        <CardBack
                          className="aspect-[5/7] w-full shadow-xl shadow-black/50"
                          imageUrl={pull.categoryBackingImageUrl}
                        />
                        <span className="absolute inset-x-0 bottom-0 rounded-b-2xl bg-gradient-to-t from-black/85 to-transparent px-1.5 pb-2 pt-6 text-center font-display text-[9px] font-bold uppercase tracking-[0.15em] text-brand-pink">
                          Tap to flip
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {pulls.items.some((p) => p.revealed) && (
                <div className="space-y-2">
                  {unrevealed.length > 0 && (
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/35">Already opened</p>
                  )}
                  {pulls.items
                    .filter((p) => p.revealed)
                    .map((pull) => (
                      <div
                        key={pull.id}
                        className="flex items-center gap-3 rounded-2xl border border-white/5 bg-surface/70 p-3 transition-colors hover:border-white/15"
                      >
                        <ProductImage src={pull.productImage} alt="" className="h-12 w-12 shrink-0 rounded-xl" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium">{pull.productName}</p>
                          <p className="text-[10px] text-white/40">
                            {pull.packName} · {fmtDateTimeDMY(pull.at)}
                          </p>
                        </div>
                        <span
                          className="rounded-full px-2 py-0.5 text-[9px] font-bold"
                          style={{ background: `${pull.tierColor}22`, color: pull.tierColor }}
                        >
                          {pull.tierName}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Trades */}
      {activeTab === "trades" && (
        <>
          {!trades?.items.length ? (
            <EmptyState icon={ArrowLeftRight} title="No trades yet" hint="Trade offers you send and receive appear here." />
          ) : (
            <div className="space-y-2.5">
              {trades.items.map((trade) => {
                const incoming = trade.toUserId === user?.id;
                const expiry = formatExpiry(trade.expiresAt);
                return (
                  <div key={trade.id} className="rounded-2xl border border-white/5 bg-surface p-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold">
                        {incoming ? `From ${trade.fromName}` : `To ${trade.toName}`}
                      </p>
                      <Badge color={tradeStatusColor(trade.status)}>
                        {tradeStatusLabel(trade, incoming)}
                      </Badge>
                    </div>
                    {expiry && (trade.status === "PENDING" || trade.status === "AWAITING_CONFIRM") && (
                      <p className="mt-1 text-[10px] text-amber-300">{expiry}</p>
                    )}
                    {trade.message && <p className="mt-1 text-[11px] italic text-white/50">“{trade.message}”</p>}
                    <div className="mt-2.5 grid grid-cols-2 gap-2 text-[10px]">
                      <div className="rounded-xl bg-white/5 p-2">
                        <p className="mb-1 text-white/40">{incoming ? "They give" : "You give"}</p>
                        {trade.offerItems.map((i) => (
                          <p key={i.vaultItemId} className="truncate text-white/80">
                            • {i.name}
                          </p>
                        ))}
                        {trade.offerCredits > 0 && <p className="text-brand-pink">+ {trade.offerCredits} credits</p>}
                      </div>
                      <div className="rounded-xl bg-white/5 p-2">
                        <p className="mb-1 text-white/40">{incoming ? "You give" : "They give"}</p>
                        {trade.requestItems.map((i) => (
                          <p key={i.vaultItemId} className="truncate text-white/80">
                            • {i.name}
                          </p>
                        ))}
                        {trade.requestCredits > 0 && (
                          <p className="text-brand-pink">+ {trade.requestCredits} credits</p>
                        )}
                      </div>
                    </div>
                    {(trade.status === "PENDING" || trade.status === "AWAITING_CONFIRM") && (
                      <div className="mt-2.5 flex gap-2">
                        {trade.status === "PENDING" && incoming && (
                          <>
                            <Button className="flex-1" disabled={busy} onClick={() => respondTrade(trade.id, "accept")}>
                              Accept
                            </Button>
                            <Button
                              variant="outline"
                              className="flex-1"
                              disabled={busy}
                              onClick={() => respondTrade(trade.id, "decline")}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                        {trade.status === "AWAITING_CONFIRM" && !incoming && (
                          <Button className="flex-1" disabled={busy} onClick={() => respondTrade(trade.id, "confirm")}>
                            Final confirm
                          </Button>
                        )}
                        {(trade.status === "PENDING" && !incoming) || trade.status === "AWAITING_CONFIRM" ? (
                          <Button
                            variant="ghost"
                            className="flex-1"
                            disabled={busy}
                            onClick={() => respondTrade(trade.id, "cancel")}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FlexxiSlotPlaceholder({ title, hint }: { title: string; hint: string }) {
  return (
    <div
      className="flex aspect-square flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-3 text-center"
      aria-hidden
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-white/40">{title}</p>
      <p className="mt-1.5 text-[10px] leading-snug text-white/30">{hint}</p>
    </div>
  );
}

/**
 * Same card-back-then-flip ceremony as a live pack open, for pulls that were
 * bought through checkout and revealed later.
 */
function PullRevealOverlay({ pull, onClose }: Readonly<{ pull: PullDto | null; onClose: () => void }>) {
  useBodyLock(!!pull);

  const outcome: PullOutcome | null = pull
    ? {
        pullId: pull.id,
        tierName: pull.tierName,
        tierColor: pull.tierColor,
        productName: pull.productName,
        productImage: pull.productImage,
        categoryBackingImageUrl: pull.categoryBackingImageUrl ?? null,
        estValue: pull.value,
        serverSeed: pull.proof.serverSeed,
        serverSeedHash: pull.proof.serverSeedHash,
        clientSeed: pull.proof.clientSeed,
        nonce: pull.proof.nonce,
      }
    : null;

  return (
    <AnimatePresence>
      {outcome && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Reveal pull"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-noise fixed inset-0 z-[90] flex overflow-y-auto bg-brand-dark/98 bg-mesh-deep"
        >
          <Particles count={18} />
          <div className="relative z-10 flex flex-1">
            <RevealStage pulls={[outcome]} onDone={onClose} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
